/* eslint-disable @next/next/no-img-element */
import { ButtonBase, IconButton } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Modal from "react-modal";
// eslint-disable-next-line import/no-extraneous-dependencies
import PrismaZoom from "react-prismazoom";

import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PropTypes from "prop-types";
import { colors } from "common";
import { useMemo, useState } from "react";
import { Theme, useTheme } from "@mui/material/styles";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: "100%",
    width: "100%",
    overflow: "hidden",
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  zoom: {
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imgClass: {
    width: "100%",
    height: "100%",
    backgroundSize: "cover",
    objectFit: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center center",
  },
  closeButton: {
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    position: "absolute",
    top: theme.spacing(3),
    right: theme.spacing(3),
  },
  prevButton: {
    // backgroundColor: colors.white,
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    [theme.breakpoints.down("md")]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  nextButton: {
    // backgroundColor: colors.pink[400],
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    [theme.breakpoints.down("md")]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
}));

const customModalDesktopStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    zIndex: 1000,
  },
  content: {
    position: "absolute",
    top: "30px",
    left: "30px",
    right: "30px",
    bottom: "30px",
    border: "1px solid",
    background: "#fff",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    borderRadius: "4px",
    outline: "none",
    padding: "30px",
    height: "100%",
    backgroundColor: colors.common.black,
  },
};

const customModalMobileStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    zIndex: 1000,
  },
  content: {
    position: "absolute",
    top: "10px",
    left: "10px",
    right: "10px",
    bottom: "10px",
    border: "1px solid",
    background: "#fff",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    borderRadius: "4px",
    outline: "none",
    padding: "30px",
    height: "100%",
    backgroundColor: colors.common.black,
  },
};

const ImagesGallery = ({ images = [], onClose, isOpen, activeIndex = 0 }) => {
  const classes = useStyles();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const [activeImageSrc, setActiveImageSrc] = useState("");

  const onRequestClose = () => {
    onClose();
  };

  const onOpen = () => {
    if (
      (activeIndex !== undefined || activeIndex !== null) &&
      images.length > -1
    ) {
      const newActiveImg =
        images[activeIndex] && images[activeIndex].url
          ? images[activeIndex].url
          : "";
      setActiveImageSrc(newActiveImg);
    }
  };

  const maxIndex = useMemo(() => images.length - 1, [images]);

  const currentIndex = useMemo(
    () => images.findIndex((x) => x.url === activeImageSrc) || 0,
    [images, activeImageSrc]
  );

  const isNextBtnHidden = useMemo(() => {
    if (activeImageSrc) {
      if (currentIndex >= maxIndex) {
        return true;
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, activeImageSrc]);

  const onNextPress = () => {
    const newIndex = currentIndex + 1;
    const newSrc =
      images[newIndex] && images[newIndex].url ? images[newIndex].url : "";
    setActiveImageSrc(newSrc);
  };

  const isPrevBtnHidden = useMemo(() => {
    if (activeImageSrc) {
      if (currentIndex <= 0) {
        return true;
      }
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, activeImageSrc]);

  const onPrevPress = () => {
    const newIndex = currentIndex - 1;
    const newSrc =
      images[newIndex] && images[newIndex].url ? images[newIndex].url : "";
    setActiveImageSrc(newSrc);
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      shouldReturnFocusAfterClose
      onAfterOpen={onOpen}
      onRequestClose={onRequestClose}
      style={isMobile ? customModalMobileStyles : customModalDesktopStyles}
    >
      <div className={classes.root}>
        <PrismaZoom className={classes.zoom}>
          <img
            className={classes.imgClass}
            alt="gallery_img"
            src={activeImageSrc}
          />
        </PrismaZoom>
        {isPrevBtnHidden ? null : (
          <ButtonBase onClick={onPrevPress} className={classes.prevButton}>
            <KeyboardArrowLeftIcon
              sx={{ color: "white" }}
              fontSize={isMobile ? "small" : "large"}
              classes={{ colorPrimary: colors.white }}
            />
          </ButtonBase>
        )}

        {isNextBtnHidden ? null : (
          <ButtonBase onClick={onNextPress} className={classes.nextButton}>
            <KeyboardArrowRightIcon
              sx={{ color: "white" }}
              fontSize={isMobile ? "small" : "large"}
              classes={{ colorPrimary: colors.white }}
            />
          </ButtonBase>
        )}

        <IconButton onClick={onRequestClose} className={classes.closeButton}>
          <CloseIcon
            sx={{ color: "white" }}
            fontSize={isMobile ? "small" : "large"}
            classes={{ colorPrimary: colors.white }}
          />
        </IconButton>
      </div>
    </Modal>
  );
};

// NOTE: Since we dont have types.
// images props should look like this.
// [
//   {
//     url: 'imgSrc url'
//   },
//   {
//     url: 'imgSrc url'
//   },
//   {
//     url: 'imgSrc url'
//   },
//   {
//     url: 'imgSrc url'
//   },
// ]

ImagesGallery.propTypes = {
  images: PropTypes.any,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  activeIndex: PropTypes.number,
};

export default ImagesGallery;
